"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Globe as Globe2, Clock, MessageSquare, UserCheck, ArrowUpRight, CircleCheck as CheckCircle2, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const deliveryPillars = [{
  icon: UserCheck,
  title: "Direct consultant access",
  description: "Every audit, technical recommendation, and roadmap is delivered directly by me, ensuring dedicated attention and senior-level technical oversight."
}, {
  icon: Clock,
  title: "Timezone flexibility",
  description: "I structure my consulting schedule around key UK, European, and Gulf time zones, providing dedicated windows for real-time video sessions and rapid responses."
}, {
  icon: MessageSquare,
  title: "Direct asynchronous communication",
  description: "Get direct access via Slack, Teams, or clear Loom video walk-throughs. You receive frank answers and concise updates without bureaucratic back-and-forth."
}, {
  icon: Globe2,
  title: "Multi-market precision",
  description: "Whether targeting local audiences or building multi-region organic visibility, I align hreflang, local search signals, and technical architecture for each territory."
}];
const operatingZones = [{
  region: "United Kingdom & Europe",
  hours: "08:30 - 18:00 GMT",
  status: "Primary Base"
}, {
  region: "Middle East & Gulf (UAE, KSA)",
  hours: "11:30 - 21:00 GST",
  status: "Active Work Window"
}, {
  region: "US East Coast (Overlapping)",
  hours: "13:00 - 17:30 EST",
  status: "Coordinated Calls"
}];
export default function DeliveryModel() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="remotedeliverymodel" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-14 lg:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary">
            Remote International Delivery
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-default">
            Direct SEO consulting, wherever you operate.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            You work directly with me. No account managers, no middle layers, and no delegated tasks.
            I advise and execute across your active working hours with complete technical accountability.
          </p>
        </div>

        {/* Main Grid: Pillars and Timezone Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* 4 Pillars - Left / Main Area */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {deliveryPillars.map((pillar, i) => {
            const IconComponent = pillar.icon;
            return <motion.div key={pillar.title} variants={itemVariants} data-index={i} className="h-full">
                  <Card className="h-full border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                    <CardHeader className="space-y-3 pb-3">
                      <div className="size-11 rounded-lg bg-secondary text-primary flex items-center justify-center">
                        <IconComponent className="size-5" />
                      </div>
                      <CardTitle className="text-lg font-semibold text-card-foreground font-default">
                        {pillar.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {pillar.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>

          {/* Right Panel: Working Hours & Real Market Examples */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Global Coordination Card */}
            <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm">
              <div className="flex items-center justify-between gap-4 mb-5">
                <h3 className="text-base font-semibold text-foreground flex items-center gap-2 font-default">
                  <Clock className="size-4 text-primary" />
                  Timezone alignment
                </h3>
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                  <span className="size-2 rounded-full bg-chart-4" />
                  Available for calls
                </span>
              </div>

              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Working windows are coordinated to accommodate international stakeholders, scheduled calls, and development sprints.
              </p>

              <div className="space-y-4">
                {operatingZones.map((zone, i) => <div key={zone.region} data-index={i} className="p-3.5 rounded-lg bg-background border border-border flex items-center justify-between gap-3 text-xs sm:text-sm">
                    <div>
                      <p className="font-medium text-foreground">{zone.region}</p>
                      <p className="text-muted-foreground text-xs mt-0.5">{zone.hours}</p>
                    </div>
                    <Badge variant="outline" className="border-border text-foreground text-xs">
                      {zone.status}
                    </Badge>
                  </div>)}
              </div>

              <Separator className="my-6" />

              {/* Verified Location Links */}
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
                  Location-Specific Case Examples
                </p>
                <div className="space-y-2">
                  <Link to="/InternationalSeoPage" className="group flex items-center justify-between p-3 rounded-lg border border-border bg-background hover:border-primary/50 text-foreground transition-colors">
                    <div className="flex items-center gap-2.5">
                      <Globe2 className="size-4 text-primary" />
                      <div>
                        <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                          International SEO Consultancy
                        </span>
                        <p className="text-xs text-muted-foreground">Multi-region architecture & hreflang execution</p>
                      </div>
                    </div>
                    <ArrowUpRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </Link>

                  <Link to="/SeoFreelancerDubaiPage" className="group flex items-center justify-between p-3 rounded-lg border border-border bg-background hover:border-primary/50 text-foreground transition-colors">
                    <div className="flex items-center gap-2.5">
                      <MapPin className="size-4 text-accent" />
                      <div>
                        <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                          SEO Freelancer Dubai & UAE
                        </span>
                        <p className="text-xs text-muted-foreground">Targeted market case study & consulting setup</p>
                      </div>
                    </div>
                    <ArrowUpRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </Link>

                  <Link to="/TechnologyAndSaasPage" className="group flex items-center justify-between p-3 rounded-lg border border-border bg-background hover:border-primary/50 text-foreground transition-colors">
                    <div className="flex items-center gap-2.5">
                      <Globe2 className="size-4 text-primary" />
                      <div>
                        <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                          Technology & SaaS Clients
                        </span>
                        <p className="text-xs text-muted-foreground">International remote SaaS & software consulting</p>
                      </div>
                    </div>
                    <ArrowUpRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Direct Access Callout */}
            <div className="p-6 rounded-xl bg-secondary/70 border border-secondary text-secondary-foreground flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-primary" />
                  International SEO consulting
                </p>
                <p className="text-xs text-muted-foreground">
                  Discuss your target markets, technical scope, and project timeline.
                </p>
              </div>
              <Button asChild size="sm" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactPage">
                  Book a Consultation
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}